"use client";

import * as React from "react";
import Box from "@mui/material/Box";
import GlobalStyles from "@mui/material/GlobalStyles";

import { layoutConfig } from "../config";
import { useSettings } from "../../../hooks/use-settings";
import { partnerLayoutConfig } from "../config";
import { MainNav } from "./main-nav";
import { SideNav } from "./side-nav";
import {
  CustomerRoleName,
  PrimaryGroups,
  useGroupStore,
  useProfileStore,
  useRoleStore,
} from "../../../store/ProfileStore";
import { decodeFromBase64 } from "../../../functions/misc";
import { PartnerSideNav } from "./partner-side-nav";
import { useEffect, useState } from "react";
import { paths } from "../../../paths";
import { NavItemConfig } from "../../../types/nav";
import { useFlags } from "launchdarkly-react-client-sdk";
import { FeatureFlags } from "../../../types/featureFlags";
import useEnrollmentStore, { ProtectionPlanName } from "../../../store/EnrollmentStore";

export interface VerticalLayoutProps {
  hideSidebar: boolean;
  children?: React.ReactNode;
}

export function VerticalLayout({
  children,
  hideSidebar,
}: VerticalLayoutProps): React.ReactElement {
  const { settings } = useSettings();
  const { primaryGroup } = useGroupStore();
  const { primaryProfile } = useProfileStore();
  const { customerRole } = useRoleStore();
  const flagList: FeatureFlags = useFlags();
  const [protectedNavItems, setProtectedNavItems] = useState<NavItemConfig[]>([]);
  const { protectionPlanEnrollment } = useEnrollmentStore();

  useEffect(() => {
    if (customerRole) {
      const navItems = layoutConfig.navItems.map((navGroup: any) => {
        const navGroupList = { ...navGroup };
        navGroupList.items = navGroup.items
          .filter((item: any) => canAccess(item, customerRole))
          .map((item: any) => ({
            ...item,
            label: getSubscriptionLabel(item.key),
          }));
        return navGroupList;
      });

      setProtectedNavItems(navItems);
    }
  }, [customerRole, flagList]);

  const getSubscriptionLabel = (item: string) => {
    if (item !== "taxShield") return "";

    switch (protectionPlanEnrollment?.subscriptionPlan) {
      case ProtectionPlanName.Standard:
        return "STND";
      case ProtectionPlanName.Premium:
        return "PREM";
      case ProtectionPlanName.Professional:
        return "PRO";
      case ProtectionPlanName.ProtectionPartnerMonitoring:
        return "PTR";
      case ProtectionPlanName.Free:
        return "FREE";
      default:
        return "";
    }
  };

  const canAccess = (
    item: NavItemConfig,
    roleName: CustomerRoleName | null
  ): boolean => {
    const defaultPermission = true;
    switch (item.key) {
      case "clientPortal":
        return roleName === CustomerRoleName.TaxReliefGraduatedCustomer;
      case "taxPreparation":
        return roleName !== CustomerRoleName.TaxPrepReferredCustomer && roleName !== CustomerRoleName.CoClientCustomer && protectionPlanEnrollment?.subscriptionPlan !== ProtectionPlanName.Premium && protectionPlanEnrollment?.subscriptionPlan !== ProtectionPlanName.Professional;
      case "taxShield":
        return roleName !== CustomerRoleName.CoClientCustomer;
      default:
        return defaultPermission;
    }
  };

  return (
    <React.Fragment>
      <GlobalStyles
        styles={{
          body: {
            "--MainNav-height": "56px",
            "--MainNav-zIndex": 1000,
            "--SideNav-width": hideSidebar ? "0px" : "300px",
            "--SideNav-zIndex": 50,
            "--MobileNav-width": "320px",
            "--MobileNav-zIndex": 50,
          },
        }}
      />
      <Box
        sx={{
          bgcolor: "var(--mui-palette-background-default)",
          display: "flex",
          flexDirection: "column",
          position: "relative",
          minHeight: "100%",
        }}
      >
        {!hideSidebar && (
          <>
            {decodeFromBase64(primaryGroup) === PrimaryGroups.Partner ? (
              <PartnerSideNav
                color={settings.navColor}
                items={partnerLayoutConfig.navItems}
              />
            ) : (
              <SideNav
                color={settings.navColor}
                items={
                  protectedNavItems && protectedNavItems.length > 0 ? protectedNavItems : layoutConfig.navItems
                }
              />
            )}
          </>
        )}
        <Box
          sx={{
            display: "flex",
            flex: "1 1 auto",
            flexDirection: "column",
            pl: { lg: "var(--SideNav-width)" },
          }}
        >
          {!hideSidebar && <MainNav
            items={
              decodeFromBase64(primaryGroup) === PrimaryGroups.Partner
                ? partnerLayoutConfig.navItems
                : protectedNavItems && protectedNavItems.length > 0
                  ? protectedNavItems
                  : layoutConfig.navItems
            }
            user_type={decodeFromBase64(primaryGroup)}
          />}

          <Box
            component="main"
            sx={{
              "--Content-margin": "0 auto",
              "--Content-maxWidth": "var(--maxWidth-xl)",
              "--Content-paddingX": "24px",
              "--Content-paddingY": { xs: "24px", lg: "64px" },
              "--Content-padding":
                "var(--Content-paddingY) var(--Content-paddingX)",
              "--Content-width": "100%",
              display: "flex",
              flex: "1 1 auto",
              flexDirection: "column",
              fontSize: "inherit",
              backgroundColor: "#f7f9fa",
            }}
          >
            {children}
          </Box>
        </Box>
      </Box>
    </React.Fragment>
  );
}